import React, { useEffect } from 'react';
import { ArrowRightIcon, CalendarIcon, Loader2Icon } from 'lucide-react';
import { useLeadForm } from '../../hooks/useLeadForm';
import { useEnquiry } from '../../contexts/EnquiryContext';
import { contact, intentMessages, stateOptions, timeframeOptions, useCaseOptions } from '../../data/site';
import { email, required } from '../../utils/validation';
import { Button } from '../ui/Button';
import { TextField } from './fields/TextField';
import { SelectField } from './fields/SelectField';
import { TextAreaField } from './fields/TextAreaField';
import { TurnstileSlot } from './TurnstileSlot';
import { FormSuccess } from './FormSuccess';
export function ContactForm() {
  const {
    useCase,
    intent
  } = useEnquiry();
  const {
    values,
    errors,
    status,
    setField,
    setValues,
    submit,
    reset
  } = useLeadForm({
    name: '',
    email: '',
    phone: '',
    company: '',
    useCase: '',
    state: '',
    timeframe: '',
    message: ''
  }, {
    name: required('name'),
    email
  });
  useEffect(() => {
    setValues(current => ({
      ...current,
      useCase: useCase || current.useCase,
      message: intentMessages[intent] || current.message
    }));
  }, [useCase, intent, setValues]);
  if (status === 'success') {
    return <div className="flex min-h-[420px] flex-col justify-center gap-6" data-id="element-30" data-mp="3c5hn28mgcsb4">
        <FormSuccess title="Thanks. Your request is with our engineers." data-id="element-31" data-mp="3h65e2uaol95c">
          You’ll hear from us within 48 hours. If it’s urgent, call{' '}
          <a href={contact.phoneHref} className="font-medium text-afb-teal underline underline-offset-4" data-id="element-32" data-mp="3l6xohqvahk37">
            {contact.phoneDisplay}
          </a>
          .
        </FormSuccess>
        <div className="flex flex-wrap gap-3 pl-[52px]" data-id="element-33" data-mp="3uybxcp94rw90">
          <Button href="#resources" variant="outline-light" size="md" data-id="element-34" data-mp="3qg254lpxekc">
            Get the capability pack
          </Button>
          <Button variant="outline-light" size="md" onClick={reset} data-id="element-35" data-mp="8qu1ly997g6r">
            Send another request
          </Button>
        </div>
      </div>;
  }
  return <form onSubmit={submit} noValidate aria-labelledby="contact-form-title" className="space-y-5" data-id="element-36" data-mp="29rsaj334acwd">
      <div data-id="element-37" data-mp="24t77yx0e7h4r">
        <h3 id="contact-form-title" className="font-sora text-2xl font-semibold text-afb-ink" data-id="element-38" data-mp="24tgxyklftoag">
          Talk to an engineer
        </h3>
        {intent === 'demo-tour' && <p className="mt-2 inline-flex items-center gap-2 rounded-md bg-afb-light px-3 py-1.5 text-sm text-afb-ink" data-id="element-39" data-mp="2dtnbg6h4yo9m">
            <CalendarIcon aria-hidden="true" className="h-4 w-4 text-afb-teal" data-id="element-40" data-mp="2si06n18eg332" />
            Booking a demo tour at Bibra Lake
          </p>}
      </div>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-41" data-mp="2nlj8de7hk8c9">
        <TextField id="contact-name" label="Name" required autoComplete="name" value={values.name} error={errors.name} onChange={value => setField('name', value)} data-id="element-42" data-mp="31jhiu8iorkmh" />
        <TextField id="contact-email" label="Email" type="email" required autoComplete="email" value={values.email} error={errors.email} onChange={value => setField('email', value)} data-id="element-43" data-mp="1779l6nezcpn0" />
        <TextField id="contact-phone" label="Phone" type="tel" autoComplete="tel" value={values.phone} onChange={value => setField('phone', value)} data-id="element-44" data-mp="x0qhngntlrhn" />
        <TextField id="contact-company" label="Company" autoComplete="organization" value={values.company} onChange={value => setField('company', value)} data-id="element-45" data-mp="o6tx26ab0n4c" />
        <SelectField id="contact-use-case" label="Use case" placeholder="Choose a use case" options={useCaseOptions} value={values.useCase} onChange={value => setField('useCase', value)} data-id="element-46" data-mp="e8e8a9yr1py0" />
        <SelectField id="contact-state" label="Site location" placeholder="State or territory" options={stateOptions} value={values.state} onChange={value => setField('state', value)} data-id="element-47" data-mp="1uqblvr1elexr" />
      </div>

      <SelectField id="contact-timeframe" label="Timeframe" placeholder="When do you need power?" options={timeframeOptions} value={values.timeframe} onChange={value => setField('timeframe', value)} data-id="element-48" data-mp="3qrn9dz5abp3h" />

      <TextAreaField id="contact-message" label="Message" placeholder="Your site, current power setup and what you're trying to fix." value={values.message} onChange={value => setField('message', value)} data-id="element-49" data-mp="3htjjyrr6i86e" />

      <Button type="submit" disabled={status === 'submitting'} className="w-full" data-id="element-50" data-mp="333m4d07beqst">
        {status === 'submitting' ? <>
            <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-51" data-mp="2y1eldqo2iagz" />
            Sending…
          </> : <>
            Request a demo
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-52" data-mp="2p449q47c8jbj" />
          </>}
      </Button>

      <TurnstileSlot data-id="element-53" data-mp="14jz5mxakn15r" />

      {status === 'error' && <p role="alert" className="text-sm text-red-700" data-id="element-54" data-mp="19jljn8y84lfm">
          Please check the highlighted fields.
        </p>}

      <p className="text-xs leading-relaxed text-afb-muted" data-id="element-55" data-mp="1dmi65aqvx1ou">
        By submitting, you agree to our{' '}
        <a href={contact.privacyUrl} className="underline underline-offset-2 hover:text-afb-ink" data-id="element-56" data-mp="1ijgvlfzlskgp">
          privacy policy
        </a>
        .
      </p>
    </form>;
}